/**
 * 16-bit mono WAV of a take with a gain baked in, so the file sounds the
 * same as what was heard back. Optional extra chunks ({ id: four letters,
 * text }) go after the audio, where players ignore them; graded takes use
 * one to carry their analysis.
 */
export const encodeWav = (samples, sampleRate, gain = 1, extraChunks = []) => {
  const extras = extraChunks.map(({ id, text }) => ({ id, bytes: new TextEncoder().encode(text) }));
  const extraBytes = extras.reduce((sum, { bytes }) => sum + 8 + bytes.length + (bytes.length % 2), 0);
  const dataBytes = samples.length * 2;
  const view = new DataView(new ArrayBuffer(44 + dataBytes + extraBytes));
  const writeString = (offset, text) => {
    for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
  };
  writeString(0, 'RIFF');
  view.setUint32(4, 36 + dataBytes + extraBytes, true);
  writeString(8, 'WAVE');
  writeString(12, 'fmt ');
  view.setUint32(16, 16, true); // fmt chunk size
  view.setUint16(20, 1, true); // PCM
  view.setUint16(22, 1, true); // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true); // byte rate
  view.setUint16(32, 2, true); // block align
  view.setUint16(34, 16, true); // bits per sample
  writeString(36, 'data');
  view.setUint32(40, dataBytes, true);
  for (let i = 0; i < samples.length; i++) {
    const value = Math.max(-1, Math.min(1, samples[i] * gain));
    view.setInt16(44 + i * 2, value < 0 ? value * 0x8000 : value * 0x7FFF, true);
  }
  let offset = 44 + dataBytes;
  extras.forEach(({ id, bytes }) => {
    writeString(offset, id);
    view.setUint32(offset + 4, bytes.length, true);
    new Uint8Array(view.buffer, offset + 8, bytes.length).set(bytes);
    offset += 8 + bytes.length + (bytes.length % 2); // chunks are word-aligned
  });
  return new Blob([view], { type: 'audio/wav' });
};

// "Song - take 2026-10-05 14.32.10.wav", so repeated downloads never collide
export const takeFileName = (scoreFileName, label = 'take') => {
  const base = (scoreFileName || '')
    .split('/').pop()
    .replace(/\.(musicxml|xml|mxl)$/i, '') || 'Recording';
  const now = new Date();
  const pad = (n) => String(n).padStart(2, '0');
  const date = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
  const time = `${pad(now.getHours())}.${pad(now.getMinutes())}.${pad(now.getSeconds())}`;
  return `${base} - ${label} ${date} ${time}.wav`;
};
